import { lazy, Suspense, useEffect, useState } from 'react';
import { getOrCreateSpeechBus } from '../hooks/useAudioPlayer';
import { listDotProfiles, type DotProfileLite } from '../api/dotProfiles';

const SupershapeDot = lazy(() => import('./SupershapeDot'));

interface DotWidgetProps {
  enabled: boolean;
  activeProfileId: string;
  color: string;
  wireframe: boolean;
  sensitivity: number;
}

export function DotWidget({
  enabled,
  activeProfileId,
  color,
  wireframe,
  sensitivity,
}: DotWidgetProps): JSX.Element | null {
  const [ctx, setCtx] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [profile, setProfile] = useState<unknown>(null);

  // Acquire the shared speech bus on enable. This is the single tap
  // point for bus.el (Gemini TTS, Kokoro main TTS, music gallery,
  // voice tests) AND ZeroTTS atoms — see useAudioPlayer.ts.
  useEffect(() => {
    if (!enabled) return;
    const bus = getOrCreateSpeechBus();
    setCtx(bus.ctx);
    setAnalyser(bus.analyser);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    void listDotProfiles().then((list: DotProfileLite[]) => {
      if (cancelled) return;
      const hit =
        list.find((p) => p.id === activeProfileId) ?? list[0] ?? null;
      const merged = mergeSensitivity(hit?.profile ?? null, sensitivity);
      setProfile(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, activeProfileId, sensitivity]);

  if (!enabled || !analyser) return null;

  return (
    <div className="dot-widget" aria-hidden="true">
      <Suspense fallback={<div className="dot-widget-fallback" />}>
        <SupershapeDot
          profile={profile as never}
          analyser={analyser}
          audioContext={ctx}
          color={color}
          wireframe={wireframe}
          autoRotate
        />
      </Suspense>
    </div>
  );
}

function mergeSensitivity(profile: unknown, sensitivity: number): unknown {
  if (!profile || typeof profile !== 'object') return profile;
  const p = profile as { audio?: Record<string, unknown> };
  return {
    ...(profile as object),
    audio: {
      ...(p.audio ?? {}),
      reactiveAmount: Math.max(0, Math.min(1, sensitivity)),
    },
  };
}
